import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { selectPOIById } from "@/features/survey/poisSelectors";
import { selectCurrentSurveyId } from "@/features/survey/surveySelectors";
import { surveyPOIApi, useUpdatePOICategoryMutation } from "@/services/survey-api";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldSelect } from "@emerald-react/select";
import { updateCategoryField } from "@features/survey/poisSlice";
import { useCurrentPoiId } from "@hooks/useSurveySelectors";
import React from "react";
import PoiColorPicker from "./PoiColorPicker";
import PoiIconSelector from "./PoiIconSelector";
import PoiLogoSection from "./PoiLogoSection";
import PoiPointSizeSlider from "./PoiPointSizeSlider";

const PoiDesignForm = () => {
  const surveyId = useAppSelector(selectCurrentSurveyId);
  const dispatch = useAppDispatch();
  const currentPoiId = useCurrentPoiId();
  const currentPOI = useAppSelector((state) =>
    selectPOIById(currentPoiId)(state),
  );
  const categoryId = currentPOI?.categoryId || "";
  const category = currentPOI?.category;
  const [updatePOICategory] = useUpdatePOICategoryMutation();

  const pinTypeOptions = [
    { value: "ICON", label: "Icon" },
    { value: "LOGO", label: "Logo" },
  ];
  const isLogo = (category?.pinType || "ICON") === "LOGO";

  const handlePinTypeChange = (
    _event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    value: string | string[],
  ) => {
    const pinType = value as "ICON" | "LOGO";
    
    // 1. Optimistic Redux update
    dispatch(
      updateCategoryField({
        categoryId,
        updates: { pinType },
      }),
    );
    
    // 2. Optimistic RTK Query cache update
    dispatch(
      surveyPOIApi.util.updateQueryData('getPOICategories', surveyId || "", (draft) => {
        const category = draft.find(c => c.extId === categoryId);
        if (category) {
          category.pinType = pinType;
        }
      })
    );
    
    // 3. Persist to backend
    updatePOICategory({
      surveyId: surveyId || "",
      categoryId,
      body: { pinType: pinType as "ICON" | "LOGO" | undefined },
    });
  };

  return (
    <section className="grid grid-flow-row gap-3">
      <h2 className="text-xl text-data">Design</h2>
      <div className="warning-message text-data bg-info-alert border-info-icon border flex p-2.5 gap-2 text-sm items-center rounded-sm">
        <EmeraldIcon
          icon="info"
          type="filled"
          variant="icon"
          className="text-info-icon"
        />
        <span>Changes made here will apply to all items in this category</span>
      </div>

      {!categoryId && (
        <div className="text-data bg-warning-alert border-warning-icon border flex p-2.5 gap-2 text-sm items-center rounded-sm">
          <EmeraldIcon
            icon="warning"
            type="filled"
            variant="icon"
            className="text-warning-icon shrink-0"
          />
          <span>
            Please select an existing category or create a new one before
            configuring the design.
          </span>
        </div>
      )}

      {categoryId && (
        <div className="grid grid-cols-2 gap-2 py-2 max-w-[1200px]">
          {/* Left column: pin-type dropdown → color picker (icon) / logo controls (logo) */}
          <div className="flex flex-col gap-4 max-w-[75%]">
            <div className="flex flex-col gap-y-2">
              <div className="text-data text-sm">Choose Icon or Logo</div>
              <EmeraldSelect
                label=""
                value={category?.pinType || pinTypeOptions[0].value}
                options={pinTypeOptions}
                onChange={handlePinTypeChange}
                className="iconLogoSelect"
              />
            </div>
            {isLogo ? <PoiLogoSection /> : <PoiColorPicker />}
          </div>

          {/* Right column: point size slider → icon selector (icon mode only) */}
          <div className="flex flex-col gap-4 max-w-[65%]">
            <PoiPointSizeSlider />
            {!isLogo && <PoiIconSelector />}
          </div>
        </div>
      )}
    </section>
  );
};

export default PoiDesignForm;
